import './OrganisationList-theme.scss';
import './GatewayOrganisationList.scss';

import { Button, Modal } from '@scuf/common';
import {
  Control,
  Select,
  SelectOption,
  SelectOptionSearch,
  Tooltip
} from '@forge/common';
import {
  IOrganisationState,
  setSelectedGatewayOrganisation,
  setSelectedOrganisation
} from './Redux/Global/globalShared';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import { IOrganisation } from '../onboarding/onboardingModels';
import { PopupTextOrg } from './PopupTextOrg';
import i18next from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface IGatewayOrganizationListProps {
  triggerComponent: string;
}

interface IOption {
  value: string;
  text: string;
  type: string | null | undefined;
  zuoraAccountNo: string | null;
}

const SELECTED_ORGANIZATION_KEY = 'selected-organization-id' as const;

const GatewayOrganisationList = (props: IGatewayOrganizationListProps) => {
  const [options, setOptions] = useState<IOption[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const dispatch = useDispatch();
  const featureFlags = useFlags();
  const {
    organisationList,
    organisationListLoading,
    gatewaySelectedOrganisation
  } = useSelector((state: IOrganisationState) => state.globalShared);
  	const isBacsFlowCustomer = featureFlags['enableBacsFlowCustomer'];
  const selectedSIOrgId =
    sessionStorage.getItem(SELECTED_ORGANIZATION_KEY) ?? '';

  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    orgId: selectedSIOrgId,
    orgType: ''
  });
  const getOrgTypefromId = (id: string) =>
    organisationList?.find((org: IOrganisation) => id === org.id)?.orgType;
  const getTriggerComponent = () => {
    let triggerComponent = props?.triggerComponent;

    if (props.triggerComponent === 'Customer Management')
      triggerComponent = PopupTextOrg({
        type: getOrgTypefromId(confirmPopup.orgId) || ''
      });

    return (
      <>
        Organization change will lead you to{' '}
        <i>
          <b>{triggerComponent || ''}</b>
        </i>{' '}
        home screen. Do you want to continue?
      </>
    );
  };

  // Function to sort organizations with distributors first
  const sortWithDistributorsFirst = (organizations: IOrganisation[]) => {
    return (
      [...organizations].sort((a, b) => {
        // First prioritize by organization type
        if (a.orgType === 'Distributor' && b.orgType !== 'Distributor') {
          return -1;
        }
        if (a.orgType !== 'Distributor' && b.orgType === 'Distributor') {
          return 1;
        }
        // If same type, sort alphabetically by name
        return a.name.localeCompare(b.name);
      }) || []
    );
  };

	useEffect(() => {
		const isBacsFlowDistributor = featureFlags['enableBacsFlowDistributor'];
		const isBacsFlowCustomer = featureFlags['enableBacsFlowCustomer'];
		const isSingleCustomer = organisationList && organisationList?.every(item => item.orgType?.toLowerCase() === 'customer');

		const filteredOrganisationList = organisationList?.filter(item => {
			if (isBacsFlowDistributor && isBacsFlowCustomer) {
				return (
					item.orgType === 'Contractor' ||
					(item.orgType === 'Customer' && isSingleCustomer) ||
					item.zuoraAccountNo !== null
				);
			}
			if (isBacsFlowDistributor) {
				return item.orgType === 'Contractor' || item.zuoraAccountNo !== null;
			}
			if (isBacsFlowCustomer) {
				return item.orgType === 'Customer' && isSingleCustomer;
			}
			return item.zuoraAccountNo !== null && item.orgType !== 'Distributor';
		});

    if (filteredOrganisationList?.length > 0 && !gatewaySelectedOrganisation) {
      const sortedOrganizations = sortWithDistributorsFirst(
        filteredOrganisationList
      );
      const defaultSelectedOrg = filteredOrganisationList.find((org) => org.id === selectedSIOrgId) ?? sortedOrganizations[0];

    const selectedOrgId =  defaultSelectedOrg.orgType?.toLocaleLowerCase() === "customer" && defaultSelectedOrg.servedBy ?  defaultSelectedOrg.servedBy : defaultSelectedOrg.id;
    sessionStorage.setItem(SELECTED_ORGANIZATION_KEY,  selectedOrgId);

      dispatch(
        setSelectedGatewayOrganisation(
          sortedOrganizations.length > 0 ? defaultSelectedOrg : null
        )
      );
    }
  }, [organisationList]);

  const orgDataToSelectOptions = useMemo(
    () => () => {
      if (organisationList?.length) {
        const filteredOrganisationList = organisationList.filter(item => {
            if (featureFlags['enableBacsFlowDistributor']) {
            return item.orgType === 'Contractor' || item.zuoraAccountNo !== null;
          } 

          else {
            return (
              item.zuoraAccountNo !== null && item.orgType !== 'Distributor'
            );
          }
        });
        return sortWithDistributorsFirst(filteredOrganisationList).map(
          (org: IOrganisation) => {
            return {
              value: org.id,
              text: org.name,
              type: org.orgType,
              zuoraAccountNo: org.zuoraAccountNo
            };
          }
        );
        //.sort((a, b) => a.text.localeCompare(b.text));
      } else {
        return [];
      }
    },
    [organisationList]
  );

  useEffect(() => {
    setOptions(orgDataToSelectOptions());
  }, [orgDataToSelectOptions]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchString.length > 0) {
        const filteredOptions = orgDataToSelectOptions().filter(option =>
          option.text.toLowerCase().includes(searchString.toLowerCase())
        );
        setOptions(filteredOptions);
      } else {
        // If search string is empty or less than 3, show all options
        setOptions(orgDataToSelectOptions());
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchString]);

  const setSelectedOrganisationList = (orgID: string) => {
    dispatch(setSelectedOrganisation(selectOrgFromDropdown(orgID)));

    dispatch(setSelectedGatewayOrganisation(selectOrgFromDropdown(orgID)));
  };

  const selectOrgFromDropdown = (orgId: string) =>
    organisationList?.find((org: IOrganisation) => orgId === org.id);

  const getPlaceholder = () => {
    if (!organisationListLoading) {
      return i18next.t('selectOrg');
    } else {
      return undefined;
    }
  };

  const handleOrgChange = (orgId: string, orgType: string) =>
    setConfirmPopup({ open: true, orgId, orgType });

  const OnChangeHandler = (selected: any) => {
    if (selected.length > 0) {
      const orgId =
        Array.isArray(selected) && selected.length > 0 ? selected[0].value : '';
      handleOrgChange(String(orgId), getOrgTypefromId(String(orgId)) || '');
    } else {
      setOptions(orgDataToSelectOptions());
      setSelectedOrganisationList(gatewaySelectedOrganisation?.id || '');
    }
  };

  const renderConfirmPopup = () => {
    return (
      <Modal
        className="gateway-org-confirm-modal-cls"
        open={confirmPopup.open}
        size="small"
        closeIcon
        onClose={() => setConfirmPopup({ open: false, orgId: '', orgType: '' })}
        closeOnDimmerClick={false} // Prevent modal from closing when clicking outside
      >
        <Modal.Header>{i18next.t('confirm')}</Modal.Header>
        <Modal.Content>{getTriggerComponent()}</Modal.Content>
        <Modal.Footer>
          <Button
            content={i18next.t('cancel')}
            className="gateway-org-btn-cls gateway-org-cancel-btn-cls"
            onClick={() =>
              setConfirmPopup({ open: false, orgId: '', orgType: '' })
            }
          />
          <Button
            content={i18next.t('confirm')}
            className="gateway-org-btn-cls gateway-org-confirm-btn-cls"
            onClick={() => {
              sessionStorage.setItem(
                SELECTED_ORGANIZATION_KEY,
                confirmPopup.orgId
              );
              setSelectedOrganisationList(confirmPopup.orgId);
              setConfirmPopup({ open: false, orgId: '', orgType: '' });
            }}
          />
        </Modal.Footer>
      </Modal>
    );
  };

  const TOOLTIP_TEXT_LENGTH_THRESHOLD = 26;
    const isCustomerFlow = () => {
    if (
      featureFlags['enableBacsFlowCustomer'] &&
      organisationList &&
       organisationList?.every(item => item.orgType?.toLowerCase() === 'customer')
    ) {
      return true;
    }
    return false;
  };
  return (
    <>

      <div className={`orglist flex flex-row h-screen items-center ${isCustomerFlow() && 'w-auto'}`}>
        {!isCustomerFlow() && (
          <>
        <Control hideSpacing= {true}>
          <Select
            height={5}
            defaultValue={selectedSIOrgId}
            id="org-select"
            onBlur={() => {
              setSelectedOrganisationList(
                gatewaySelectedOrganisation?.id || ''
              );
              setOptions(orgDataToSelectOptions());
            }}
            onChange={e => {
              const orgId = (e?.target?.value ?? e) as string;
              handleOrgChange(orgId, getOrgTypefromId(orgId) || '');
            }}
            placeholder="Select a orananisation"
            size="regular"
            search={
              <SelectOptionSearch
                label="Search shapes"
                onChange={e => setSearchString(e.target.value)}
                placeholder="Search"
              />
            }
            value={selectedSIOrgId || gatewaySelectedOrganisation?.id || ''}
            style={{ border: 'none' }}
          >
            {options.map(option => {
              const showTooltip =
                option.text.length > TOOLTIP_TEXT_LENGTH_THRESHOLD;
              if (option.type === null) {
                if (
                  option.zuoraAccountNo === null ||
                  option.zuoraAccountNo === ''
                ) {
                  return null; // Skip rendering this option
                }
              }
              return (
                <SelectOption
                  key={option.value}
                  value={option.value}
                  label={option.text}
                  className="org-list-selector-option"
                >
                  {showTooltip ? (
                    <Tooltip content={option.text}>
                      <span className="option-name">{option.text}</span>
                    </Tooltip>
                  ) : (
                    <span className="option-name">{option.text}</span>
                  )}
                  <span className="option-type">
                    {option.type ? option.type : 'SI'}
                  </span>
                </SelectOption>
              );
            })}
          </Select>
        </Control>
        <div>
          <div className="org-list-selector-label-text">
            <div>
              {gatewaySelectedOrganisation?.orgType
                ? gatewaySelectedOrganisation.orgType
                : 'SI'}
            </div>
          </div>
        </div>
        </>
        )}
      </div>
      {renderConfirmPopup()}
    </>
  );
};

export default GatewayOrganisationList;
